// Larmregler — labbversion av regler som faktiskt går att driftsätta.
//
// Två regler, medvetet enkla nog att förklaras för en kund på en rad var:
//
//   1. Excursion — värdet får gå över armeringsnivån, men måste vara tillbaka under
//                  återställningsnivån före deadline. Takgränsen larmar direkt.
//   2. Budget    — tid över gräns inom ett rullande fönster (se exposureModel).
//
// En tidigare "akut"-regel (över en gräns sammanhängande i N minuter) är borttagen:
// den är specialfallet av excursionsregeln där återställningsnivån är samma tal som
// armeringsnivån, och att ha båda förvirrade mer än det hjälpte. Skiljer man talen åt
// blir excursionsregeln dessutom bättre — den nollas inte av en enstaka dipp under
// gränsen, vilket är precis vad som gör att en pendlande enhet annars aldrig larmar.
//
// Excursionsregeln finns INTE i produktionens regelmotor idag — den kräver ett nytt
// trigger_mode plus persistat excursionsläge. Den ligger här för att kunna mätas mot
// historik innan den byggs.

import { Reading } from './recoveryModel';

const MIN_MS = 60_000;


export type RuleKind = 'excursion' | 'budget';

export interface AlarmEvent {
    ts: number;
    rule: RuleKind;
    /** kort förklaring, visas i händelsetabellen */
    reason: string;
    /** avläst värde när larmet gick, om känt */
    value: number | null;
}

// ── 1. Excursion: uppgång tillåten, återhämtning obligatorisk ────────────────

export interface ExcursionParams {
    /** över denna nivå startar en excursion */
    armLevel: number;
    /** under denna nivå räknas excursionen som återhämtad (ska ligga UNDER armLevel — hysteres) */
    recoverLevel: number;
    /** över denna nivå larmar det direkt, oavsett deadline (utelämnad = av) */
    ceilingLevel?: number;
    /** hur lång tid excursionen får ta innan larm */
    deadlineMinutes: number;
    /**
     * Räkna bara den tid då det INTE blir bättre. Klockan pausas medan temperaturen
     * sjunker och går igen så fort den står still eller stiger. Storleken på fallet
     * spelar ingen roll — bara riktningen.
     *
     * Skillnaden mot att nollställa klockan är hela poängen: en trött kompressor som
     * pendlar upp och ner utan att nå hem samlar på sig tid vid varje uppgång tills
     * det larmar. Hade varje fall nollat klockan hade den aldrig larmat.
     */
    extendWhileFalling?: boolean;
}

export interface Excursion {
    start: number;
    end: number;
    /** högsta värde under excursionen */
    peak: number;
    /** lägsta värde efter toppen */
    trough: number;
    outcome: 'recovered' | 'no-recovery' | 'ceiling' | 'open';
    firedAt: number | null;
    /** största lucka mellan två avläsningar inuti excursionen — hur mycket utfallet
     *  vilar på antagandet "den låg kvar uppe medan sensorn var tyst" */
    maxGapMs: number;
}

/**
 * Kör excursionsregeln över serien. Varje excursion börjar när värdet passerar
 * armeringsnivån och slutar när det faller under återställningsnivån. Larmar om
 * takgränsen bryts, eller om återhämtningen inte är klar före deadline.
 */
export function runExcursions(rows: Reading[], params: ExcursionParams): Excursion[] {
    const deadlineMs = Math.max(0, params.deadlineMinutes) * MIN_MS;
    const extendWhileFalling = params.extendWhileFalling ?? false;
    const ceiling = params.ceilingLevel;
    const out: Excursion[] = [];
    if (rows.length === 0) return out;

    const s = [...rows].sort((a, b) => a.ts - b.ts);

    let ex: Excursion | null = null;
    // Uppsamlad tid som räknas mot deadline. Utan extendWhileFalling är det helt
    // enkelt klocktiden sedan excursionen började; med den räknas bara de intervall
    // där temperaturen inte sjönk.
    let accruedMs = 0;
    let prevValue = 0;

    for (const r of s) {
        if (!ex) {
            if (r.value > params.armLevel) {
                ex = {
                    start: r.ts, end: r.ts, peak: r.value, trough: r.value,
                    outcome: 'open', firedAt: null, maxGapMs: 0,
                };
                accruedMs = 0;
                prevValue = r.value;
            }
            continue;
        }

        const dt = r.ts - ex.end;
        const falling = r.value < prevValue;
        if (!extendWhileFalling || !falling) accruedMs += dt;

        ex.maxGapMs = Math.max(ex.maxGapMs, dt);
        ex.end = r.ts;
        prevValue = r.value;

        if (r.value > ex.peak) ex.peak = r.value;
        if (r.value < ex.trough) ex.trough = r.value;

        if (ceiling != null && r.value > ceiling && ex.firedAt === null) {
            ex.firedAt = r.ts;
            ex.outcome = 'ceiling';
        }

        if (r.value < params.recoverLevel) {
            if (ex.outcome === 'open') ex.outcome = 'recovered';
            out.push(ex);
            ex = null;
            continue;
        }

        if (ex.firedAt === null && accruedMs >= deadlineMs) {
            ex.firedAt = r.ts;
            ex.outcome = 'no-recovery';
        }
    }
    if (ex) out.push(ex);
    return out;
}

// ── Utfall: fångade incidenter och falsklarm ──────────────────────────────────

export interface Incident {
    start: number;
    end: number;
}

export interface IncidentOutcome {
    incident: Incident;
    /** första larm inom incidentfönstret, eller null om inget larm kom */
    firstEventTs: number | null;
    /** minuter från incidentens början till larmet */
    latencyMinutes: number | null;
    rule: RuleKind | null;
}

export interface Outcome {
    rows: IncidentOutcome[];
    caught: number;
    missed: number;
    /** larm som inte ligger i något markerat incidentfönster */
    falseAlarms: number;
    falseAlarmsPerWeek: number;
    spanDays: number;
}

/**
 * Mäter utfallet: fångades varje markerad incident, hur lång tid tog det, och hur
 * många larm föll utanför alla incidenter. Utan markerade incidenter räknas allt
 * som falsklarm — vilket är det korrekta svaret för en period du intygar är felfri.
 */
export function evaluateOutcome(events: AlarmEvent[], incidents: Incident[], spanMs: number): Outcome {
    const sorted = [...events].sort((a, b) => a.ts - b.ts);
    const inAny = (ts: number) => incidents.some(i => ts >= i.start && ts <= i.end);

    const rows: IncidentOutcome[] = incidents.map(incident => {
        const hit = sorted.find(e => e.ts >= incident.start && e.ts <= incident.end);
        return {
            incident,
            firstEventTs: hit?.ts ?? null,
            latencyMinutes: hit ? (hit.ts - incident.start) / MIN_MS : null,
            rule: hit?.rule ?? null,
        };
    });

    const falseAlarms = sorted.filter(e => !inAny(e.ts)).length;
    const spanDays = spanMs / 86_400_000;

    return {
        rows,
        caught: rows.filter(r => r.firstEventTs !== null).length,
        missed: rows.filter(r => r.firstEventTs === null).length,
        falseAlarms,
        falseAlarmsPerWeek: spanDays > 0 ? falseAlarms * 7 / spanDays : 0,
        spanDays,
    };
}
